import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

export const FILE_LABEL_OPTIONS = [
  {
    value: "input",
    label: "Input",
    description: "Data used to build assessments or train models",
  },
  {
    value: "metadata",
    label: "Metadata",
    description: "Reference, enrichment or verification attributes",
  },
  {
    value: "output",
    label: "Output",
    description: "Outcomes, decisions, repayments, scores or recommendations",
  },
];

export const inferFileLabels = (fileName = "") => {
  const value = String(fileName).toLowerCase();
  if (/output|outcome|label|repay|decision|funded|default|recommendation/.test(value))
    return ["output"];
  if (/metadata|enrich|reference|lookup|verification/.test(value))
    return ["input", "metadata"];
  return ["input"];
};

const fileKey = (file) =>
  `${file.name}-${file.size}-${file.lastModified || 0}`;

export const labelSelectedFiles = (files = []) =>
  files.map((file) => ({
    id: fileKey(file),
    file,
    labels: inferFileLabels(file.name),
  }));

export default function PaymentPlatformFileUploadDialog({
  open,
  files,
  busy = false,
  onClose,
  onUpload,
  title = "Upload data",
  description = "Label each file so CrediArc knows how to use it. You can upload different data types together.",
}) {
  const [labelledFiles, setLabelledFiles] = useState([]);

  useEffect(() => {
    if (open) setLabelledFiles(labelSelectedFiles(files));
  }, [files, open]);

  const updateLabels = (id, labels) =>
    setLabelledFiles((current) =>
      current.map((item) => (item.id === id ? { ...item, labels } : item)),
    );

  const removeFile = (id) =>
    setLabelledFiles((current) => current.filter((item) => item.id !== id));

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ color: "#64748b", fontSize: 14, mb: 2 }}>
          {description}
        </Typography>
        <Box sx={{ display: "grid", gap: 1.25 }}>
          {labelledFiles.map((item) => (
            <Paper
              key={item.id}
              variant="outlined"
              sx={{
                p: 1.5,
                display: "grid",
                gridTemplateColumns: { xs: "1fr auto", md: "minmax(220px, 1fr) 310px auto" },
                alignItems: "center",
                gap: 1.5,
                borderColor: "#dbe7f3",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 800, color: "#0b2a4a" }}>
                  {item.file.name}
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                  {(item.file.size / 1024 / 1024).toFixed(1)} MB
                </Typography>
              </Box>
              <FormControl
                size="small"
                sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}
              >
                <InputLabel>File labels</InputLabel>
                <Select
                  multiple
                  label="File labels"
                  value={item.labels}
                  onChange={(event) => updateLabels(item.id, event.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {selected.map((label) => <Chip key={label} size="small" label={label} />)}
                    </Box>
                  )}
                >
                {FILE_LABEL_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={item.labels.includes(option.value)} />
                    <ListItemText
                      primary={option.label}
                      secondary={option.description}
                      primaryTypographyProps={{ fontSize: 14, fontWeight: 700 }}
                      secondaryTypographyProps={{ fontSize: 11.5 }}
                    />
                  </MenuItem>
                ))}
                </Select>
              </FormControl>
              <IconButton
                aria-label={`Remove ${item.file.name}`}
                disabled={busy}
                onClick={() => removeFile(item.id)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Paper>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || labelledFiles.length === 0 || labelledFiles.some((item) => !item.labels.length)}
          onClick={() => onUpload(labelledFiles)}
        >
          {busy
            ? "Uploading…"
            : `Continue with ${labelledFiles.length} file${labelledFiles.length === 1 ? "" : "s"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
